import type { ActionType } from '@/shared/components/ActionsDropdown'
import type {
	ApplyAction,
	SettingsTemplateCreateModel,
	SettingsTemplateModel,
	SettingsTemplateType,
} from '@/shared/generated'

import { PlusIcon, PuzzleIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import {
	useBeforeGroupChange,
	useOptimizationIsReadyForChange,
} from '@/entities/optimization'
import {
	createSettingsTemplateSchemaAsync,
	useApplySettingsTemplateMutation,
	useCreateSettingsTemplateMutation,
} from '@/entities/settings-template'

import { Button } from '@/shared/components/ui/button'
import { confirm } from '@/shared/components/ui/confirmer'
import { ampli } from '@/shared/lib/ampli'
import { useModals } from '@/shared/lib/modal-manager'
import { queryClient } from '@/shared/lib/query-client'
import { processingMessage } from '@/shared/tools/message'

export function useTemplateActions({
	allowedTypes,
	applyAction,
	templatePayload,
	handleUseTemplate,
	id: entityId,
	optimizationGroupId,
	onBeforeApply,
}: {
	/**
	 * Allowed types to apply the template to
	 * @note Mostly used for pricing campaigns tactics to apply different tactics types to one pc
	 */
	allowedTypes: SettingsTemplateType[]
	applyAction: ApplyAction
	/**
	 * The template payload to create a new tempalte from current entity
	 */
	templatePayload: SettingsTemplateCreateModel
	/**
	 * The entity id to apply the template to
	 */
	id?: string
	/**
	 * The optimization group id to apply the template to
	 */
	optimizationGroupId?: string
	handleUseTemplate?: (template: SettingsTemplateModel) => void
	onBeforeApply?: () => Promise<boolean>
}): ActionType[] {
	const { t } = useTranslation()

	const { onMutate } = useBeforeGroupChange(optimizationGroupId)
	const { showModal } = useModals()
	const isOptimizationReady =
		useOptimizationIsReadyForChange(optimizationGroupId)
	const isGlobal = !optimizationGroupId
	const isReadyToChange = isGlobal || Boolean(isOptimizationReady)
	const createTemplateMutation = useCreateSettingsTemplateMutation({
		onMutate() {
			toast.loading(processingMessage(t), { id: 'saving_template' })
		},
		onSuccess() {
			queryClient.invalidateQueries({ queryKey: ['settings-templates'] })
		},
		onSettled() {
			toast.dismiss('saving_template')
		},
	})
	const applyTemplateMutation = useApplySettingsTemplateMutation({
		onMutate,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['optimizations'] })
			queryClient.invalidateQueries({ queryKey: ['pricing-campaigns'] })
			queryClient.invalidateQueries({
				queryKey: ['pricing-campaigns-overview'],
			})
			queryClient.invalidateQueries({ queryKey: ['pricing-campaign'] })
		},
	})
	const handleOpenTemplate = ({ id }: { id: string }) => {
		showModal('SETTINGS_TEMPLATE_MODAL', {
			id,
			template_type: templatePayload.template_type,
		})
	}
	const handleSaveAsTemplate = async () => {
		ampli.pcSaveSettingAsTemplateClicked()

		// The payload is raw form state, which holds RB tactic params in the
		// normalized (`pricing_rule_id`) shape. The template contract owns the
		// conversion back to the branch the API accepts, so parse through it rather
		// than posting form state and getting a 422.
		const parsed =
			await createSettingsTemplateSchemaAsync.safeParseAsync(templatePayload)

		if (!parsed.success) {
			toast.error(
				t('toast.entity.failed', {
					entity: t('entity.template', { defaultValue: 'Template' }),
				}),
				{ duration: 5000 }
			)

			return
		}

		createTemplateMutation.mutate(parsed.data as SettingsTemplateCreateModel, {
			onError: () =>
				toast.error(
					t('toast.entity.failed', {
						entity: t('entity.template', { defaultValue: 'Template' }),
					}),
					{ duration: 5000 }
				),
			onSuccess: ({ id, name }: SettingsTemplateModel) => {
				toast.success(
					`${t('templates', { defaultValue: 'Template' })} ${t('template_saved_successfully', { defaultValue: 'was saved successfully' })}`,
					{
						description: name,
						action: (
							<Button size='sm' onClick={() => handleOpenTemplate({ id })}>
								{t('general_open', { defaultValue: 'Open' })}
							</Button>
						),
						duration: 5000,
					}
				)
			},
		})
	}
	const handleApply = async ({ id }: SettingsTemplateModel) => {
		const answer = await confirm({
			title: t('template.apply.confirm.title', {
				defaultValue: 'Apply template',
			}),
			description: t('pc.apply_template.confirm.text', {
				defaultValue:
					'Are you sure you want to apply the template and overwrite the Pricing campaign(s) settings?',
			}),
			actionText: t('general_apply'),
			cancelText: t('general_cancel'),
		})
		if (!answer) {
			return
		}
		if (onBeforeApply) {
			const confirm = await onBeforeApply()
			if (!confirm) {
				return
			}
		}
		if (entityId) {
			await toast.promise(
				applyTemplateMutation.mutateAsync({
					id,
					data: { ids: [entityId], action: applyAction },
				}),
				{
					loading: t('general_processing'),
					success: t('toast.entity.updated', {
						entity: t('entity.pricing_campaign'),
					}),
					error: t('toast.entity.failed'),
				}
			)
		}
	}

	return [
		{
			key: 'use_template',
			label: t('settings.action.use_template', {
				defaultValue: 'Use template',
			}),
			icon: <PuzzleIcon className='size-4' />,
			disabled: !isReadyToChange,
			onClick: () => {
				ampli.pcUseSettingsTemplateClicked()
				showModal('SETTING_TEMPLATES_MODAL', {
					template_types: allowedTypes,
					handleApply: handleUseTemplate!,
				})
			},
			testId: 'template-action-use-template',
		},
		{
			key: 'save_as_template',
			label: t('settings.action.save_as_template', {
				defaultValue: 'Save as template',
			}),
			icon: <PlusIcon />,
			onClick: handleSaveAsTemplate,
			testId: 'template-action-save-as-template',
		},
		{
			key: 'apply_template',
			label: t('pc.action.apply_template', { defaultValue: 'Apply template' }),
			icon: <PuzzleIcon className='size-4' />,
			disabled: !isReadyToChange,
			onClick: () => {
				ampli.pcApplyTemplateClicked()
				showModal('SETTING_TEMPLATES_MODAL', {
					template_types: allowedTypes,
					handleApply,
				})
			},
			testId: 'template-action-apply-template',
		},
	]
}
